/**
 * TrackRecordChips (J6, Wave 3) — the header chips: G9 agent track record
 * (hypothesis-ledger win rate, null-honest "No decisions yet") and the G6
 * lift chip whose MODE is server-computed: 'vs_holdout' ONLY when a holdout
 * attestation with n>0 exists, else 'unattributed' (banned causal grammar
 * without a holdout — AC-011/AC-015).
 *
 * Values render as-is; win rate and lift arrive as fractions (ledger
 * winRate = shipped/decided) and are formatted ×100 for display only.
 * Composition per .design/autonomy-facade/mockups/autonomy-page.html and
 * autonomy-edge-states.html.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyLift, AutonomyTrackRecord } from '../types/gates.types';

export interface TrackRecordChipsProps {
  trackRecord: AutonomyTrackRecord | null;
  lift: AutonomyLift | null;
}

type DotTone = 'success.main' | 'info.main' | 'divider';

function Dot({ tone }: { tone: DotTone }) {
  return (
    <Box
      component="span"
      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone, ml: 1, flex: 'none' }}
    />
  );
}

function StatChip({
  testId,
  tone,
  label,
  suffix,
  muted,
}: {
  testId: string;
  tone: DotTone;
  label: string;
  suffix?: string;
  muted?: boolean;
}) {
  return (
    <Chip
      data-testid={testId}
      variant="outlined"
      icon={<Dot tone={tone} />}
      sx={{ bgcolor: 'background.paper', ...(muted ? { color: 'text.secondary' } : {}) }}
      label={(
        <>
          {label}
          {suffix && (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' '}{suffix}
            </Box>
          )}
        </>
      )}
    />
  );
}

/** "+8.2%" / "-3.0%" — display-only formatting of a server-computed fraction. */
function formatSignedPercent(value: number): string {
  const percent = (value * 100).toFixed(1);
  return value >= 0 ? `+${percent}%` : `${percent}%`;
}

export const TrackRecordChips: React.FC<TrackRecordChipsProps> = ({ trackRecord, lift }) => {
  const hasDecisions = trackRecord !== null && trackRecord.winRate !== null;

  return (
    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
      {hasDecisions ? (
        <StatChip
          testId={AUTONOMY_TEST_IDS.g9Chip}
          tone="success.main"
          label={`Win rate ${Math.round((trackRecord!.winRate as number) * 100)}% · ${trackRecord!.decidedCount} decided`}
          suffix="(agent track record — G9)"
        />
      ) : (
        <StatChip
          testId={AUTONOMY_TEST_IDS.g9Chip}
          tone="divider"
          muted
          label="No decisions yet — win rate appears after the first decided hypothesis"
        />
      )}

      {lift && lift.mode === 'vs_holdout' && (
        <StatChip
          testId={AUTONOMY_TEST_IDS.liftChip}
          tone="info.main"
          label={
            lift.value !== undefined
              ? `Lift ${formatSignedPercent(lift.value)} vs holdout`
              : 'Lift vs holdout'
          }
          suffix="(G6)"
        />
      )}
      {lift && lift.mode === 'unattributed' && (
        <StatChip
          testId={AUTONOMY_TEST_IDS.liftChip}
          tone="divider"
          muted
          label="Lift: unattributed — no holdout arm"
        />
      )}
    </Stack>
  );
};

export default TrackRecordChips;
